import {CountLabelGeneratorMappings} from 'common/query-builder/query-block/count-menu/constants';
import {
  convertMetricToCountMenuMeasurement,
  getCountTypeForMeasurement,
} from 'common/query-builder/query-block/count-menu/util';
import {getSelectedPropertyName} from 'common/query-builder/query-block/utils/property-util';
import * as PropertiesMenu from 'common/query-builder/query-block/properties-menu/types';
import {convertBookmarkPropertyToPropertySelection} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import {getEventValueAndLabel} from 'common/report/util/events';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {MetricType} from 'common/types/reports/bookmark';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getDataGroupLabel} from 'iron/common/report/insights/util/events';

import {hasValidMetricBookmarkShowClauses} from '../validation';

/** Gets the name of an event based behavior */
export function getEventBehaviorName(behavior: BookmarkTypes.Behavior): Translatable | null {
  if (behavior.renamed) {
    return behavior.renamed;
  }
  const isCustomEvent = behavior.type === MetricType.CustomEvent;

  const {label} = getEventValueAndLabel({
    name: isCustomEvent ? null : behavior.name,
    custom: isCustomEvent,
    id: isCustomEvent ? behavior.id : null,
  });

  // in raw custom event in event selector, shape of custom event is {id, name: $custom_event:123, type: 'custom-event'}
  // we need to short circuit here to return the custom event name
  if (isCustomEvent) {
    return label ?? null;
  }

  if (behavior.id && behavior.name) {
    return behavior.name;
  }

  if (behavior.type !== MetricType.CustomEvent && behavior.type !== MetricType.Event) {
    return null;
  }

  return label ?? null;
}

function getFunnelBehaviorName(
  behavior: BookmarkTypes.Behavior,
  measurement?: BookmarkTypes.BehaviorMeasurement,
): Translatable | null {
  const behaviors = behavior.behaviors || [];
  const hasEventComparison = behaviors.some((beh) => !!beh.behaviors);

  /** Step 1 through Step 2 */
  if (hasEventComparison) {
    return ({t}: LocalizationStore) => t(`query-builder.metrics.name-funnel-comparison`, {count: behaviors.length});
  }

  if (behaviors.length < 2) {
    return null;
  }

  const firstBehavior = behaviors[0];
  const lastBehavior = behaviors[behaviors.length - 1];

  let behaviorLabel;

  if (behavior.id && behavior.name) {
    behaviorLabel = behavior.name;
  } else if (firstBehavior.name && lastBehavior.name) {
    const firstStep = getEventBehaviorName(firstBehavior);
    const lastStep = getEventBehaviorName(lastBehavior);

    /** Did A through C */
    behaviorLabel = ({t}: LocalizationStore) => t(`query-builder.metrics.name-funnel`, {firstStep, lastStep});
  } else {
    return null;
  }

  let stepLabel: string | Translatable = ``;
  if (measurement) {
    const stepIndex = measurement.stepIndex;
    if (stepIndex === 0) {
      stepLabel = ({t}: LocalizationStore) => t(`query-builder.step-selection.entering-funnel`);
    } else if (!stepIndex) {
      stepLabel = ({t}: LocalizationStore) => t(`query-builder.step-selection.all-steps`);
    } else {
      stepLabel = ({t}: LocalizationStore) =>
        t(`query-builder.step-selection.step-from-to`, {from: stepIndex, to: stepIndex + 1});
    }
    return ({t, print}: LocalizationStore) =>
      t(`query-builder.metrics.name-funnel-steps`, {behaviorLabel: print(behaviorLabel), stepLabel: print(stepLabel)});
  }
  return behaviorLabel;
}

function getSimpleBehaviorName(behavior: BookmarkTypes.Behavior): Translatable | null {
  if (behavior.id && behavior.name) {
    return behavior.name;
  }
  const behaviors = behavior.behaviors || [];
  const labels = behaviors.map(getEventBehaviorName);

  /** Single event selection */
  if (labels.length === 1) {
    return labels[0];
  } else if (labels.length <= 2) {
    /** Did A or B */
    return ({t}: LocalizationStore) =>
      t(`query-builder.metrics.name-simple`, {firstStep: labels[0], lastStep: labels[1]});
  }

  /** Did A or B or 2 others */
  return ({t}: LocalizationStore) =>
    t(`query-builder.metrics.name-simple-multiple`, {
      firstStep: labels[0],
      lastStep: labels[1],
      count: labels.length - 2,
    });
}

function getRetentionBehaviorName(behavior: BookmarkTypes.Behavior): Translatable | null {
  if (behavior.id && behavior.name) {
    return behavior.name;
  }
  const behaviors = behavior.behaviors || [];
  const firstBehavior = behaviors[0];
  const lastBehavior = behaviors[behaviors.length - 1];

  if (behaviors.length < 2 || !firstBehavior?.name || !lastBehavior?.name) {
    return null;
  }

  /** Did A then B */
  return ({t}: LocalizationStore) =>
    t(`query-builder.metrics.name-retention`, {
      firstStep: getEventBehaviorName(firstBehavior),
      lastStep: getEventBehaviorName(lastBehavior),
    });
}

function getRetentionFrequencyBehaviorName(behavior: BookmarkTypes.Behavior): Translatable | null {
  if (behavior.id && behavior.name) {
    return behavior.name;
  }

  if (behavior.behaviors?.length === 1 && behavior.behaviors[0].name) {
    /** Frequency of A */
    return ({t}: LocalizationStore) =>
      t(`query-builder.metrics.name-retention-frequency`, {
        firstStep: getEventBehaviorName(behavior.behaviors![0]),
      });
  }

  return null;
}

/**
 * Generates the behavior name for multiple behavior types.
 *
 * @example
 * Funnel: Add to Cart through Purchase
 * Funnel Comparisons: Step 1 through Step 4
 * Retention: Add to Cart then Purchase
 * Retention Frequency: Frequency of Purchase
 * Simple: Add to Cart or View Item
 * Simple: Add to Cart or View Item or 4 others
 */
function getBehaviorLabel(
  behavior: BookmarkTypes.Behavior,
  measurement?: BookmarkTypes.BehaviorMeasurement,
): Translatable | null {
  switch (behavior.type) {
    /** Profile metrics */
    case MetricType.People:
    case MetricType.Cohort: {
      const dataGroups = Object.values(dataGroupStore.value);
      const dataGroupId = measurement?.dataGroupId || behavior.dataGroupId;

      // returns All Profiles, All Company(s), All Project(s), etc..
      return (
        getDataGroupLabel(dataGroupId, dataGroups, {all: false, useDefault: false}) ??
        (({t}: LocalizationStore) => t(`query-builder.metrics.name-profiles`))
      );
    }

    /** Raw events */
    case MetricType.CustomEvent:
    case MetricType.Event:
      return getEventBehaviorName(behavior);

    /** Funnel: Did A through C */
    case MetricType.Funnel:
      return getFunnelBehaviorName(behavior, measurement);

    /** Simple event selections */
    case MetricType.Simple:
      return getSimpleBehaviorName(behavior);

    /** Did A then B */
    case MetricType.Retention:
      return getRetentionBehaviorName(behavior);

    /** Frequency of A */
    case MetricType.RetentionFrequency:
      return getRetentionFrequencyBehaviorName(behavior);

    default:
      return null;
  }
}

export function getMetricLabel(clause: BookmarkTypes.BehaviorShowClause): Translatable | null {
  if (!clause.behavior || !hasValidMetricBookmarkShowClauses([clause]) || !clause.measurement) {
    return null;
  }

  const behaviorName = getBehaviorLabel(clause.behavior, clause.measurement);

  const countType = getCountTypeForMeasurement({
    measurement: convertMetricToCountMenuMeasurement({behavior: clause.behavior, measurement: clause.measurement}),
    metricType: clause.behavior.type!,
  });

  const labelGenerator = CountLabelGeneratorMappings.get(clause.behavior.type!)!.get(countType!)!;

  const propertyAggregation = clause.measurement.property
    ? convertBookmarkPropertyToPropertySelection(clause.measurement.property, clause.behavior)
    : null;

  let aggregationLabel = labelGenerator({
    aggregationOperator: clause.measurement.math!,
    aggregationOperatorPerUser: clause.measurement.perUserAggregation ?? ``,
    percentile: clause.measurement.percentile ?? undefined,
    countUsersOnceType: clause.measurement.segmentMethod ?? ``,
    propertyName: propertyAggregation
      ? getSelectedPropertyName({
          selected: propertyAggregation! as PropertiesMenu.Property,
        })
      : ``,
    propertyValue:
      Array.isArray(propertyAggregation?.value) || !propertyAggregation ? undefined : propertyAggregation?.value,
    metricType: clause.behavior!.type!,
    pluralGroup: ``,
    group: ``,
    groupTitle: ``,
    queryOrigin: `report`,
    pluralGroupTitle: ``,
    forMetricName: true,
    withDataGroupSelect: true,
  });

  const isProfileMetric =
    clause.behavior.type === BookmarkTypes.MetricType.People ||
    clause.behavior.type === BookmarkTypes.MetricType.Cohort;

  if (
    isProfileMetric &&
    (clause.measurement.math === BookmarkTypes.PRIMITIVE_MATH_TYPES.TOTAL ||
      clause.measurement.math === BookmarkTypes.PRIMITIVE_MATH_TYPES.UNIQUE)
  ) {
    /** when looking at total profiles, makes sure we do not repeat "Profiles" in "Total Profiles of All User Profiles" */
    aggregationLabel = ({t}: LocalizationStore) => t(`query-builder.count-menu.total`);
  }

  /** property aggregations display a slightly different name structure: Sum of Prop on Event */
  if (propertyAggregation) {
    /** Sum of Price on Purchase */
    return ({t, print}: LocalizationStore) =>
      t(`query-builder.metrics.name-aggregation`, {
        metricLabel: print(behaviorName),
        aggregationLabel: print(aggregationLabel),
      });
  }

  /** Uniques of Purchase */
  return ({t, print}: LocalizationStore) =>
    t(`query-builder.metrics.name`, {
      metricLabel: print(behaviorName),
      aggregationLabel: print(aggregationLabel),
    });
}
